import * as React from 'react'
import { useMemo } from 'react'

import { classList } from 'react-classlist-helper'

import {
  CardList,
  ICardListGroup,
  ICardListItemPosition,
  useCardListCore,
} from '~/client/src/shared/components/CardList'
import {
  GroupHeader,
  GroupHeaderTrigger,
  GroupIndent,
} from '~/client/src/shared/components/GroupHeader'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import { filterSelectable } from '../filterSelectable'
import { useResourcePickerContext } from '../ResourcePickerContext'
import { useFirstInitiallySelectedId } from '../useFirstInitiallySelectedId'
import { PickerCountRow } from './PickerCountRow'
import PickerItem from './PickerItem'
import { PickerLoading } from './PickerLoading'
import PickerTree from './PickerTree'

interface IProps {
  renderItem?: (record: IResourceRecord, isSelected: boolean) => React.ReactNode
  renderGroupHeader?: (
    group: ICardListGroup<IResourceRecord>,
    onToggle: () => void,
  ) => React.ReactNode
  subtitleKey?: string
  countLabel?: string
  portalSelector?: string
}

const PickerGroupedList: React.FC<IProps> = ({
  renderItem,
  renderGroupHeader,
  subtitleKey,
  countLabel,
  portalSelector,
}) => {
  const {
    sortedRecords,
    selectedIds,
    mode,
    getGroupPresentation,
    grouping,
    toggleGroup,
    isItemDisabled,
    loading,
    getSourceRenderItem,
  } = useResourcePickerContext()

  const groupConfig = grouping?.config

  const listState = useCardListCore({
    items: sortedRecords,
    groupConfig,
  })

  const firstSelectedId = useFirstInitiallySelectedId(
    sortedRecords,
    selectedIds,
  )

  const activeListState = groupConfig ? listState : undefined

  const headersWithTopBorder = useMemo(() => {
    const groups = listState.visibleGroups ?? listState.groups
    const keys = new Set<string>()
    groups.forEach((group, index) => {
      const previous = groups[index - 1]
      if (previous && !previous.isCollapsed && previous.items.length > 0) {
        keys.add(group.key)
      }
    })
    return keys
  }, [listState.visibleGroups, listState.groups])

  // In a nested group, group.items holds only the records assigned directly to this
  // node; child-group records live in their own buckets. The select-all checkbox must
  // cover the whole subtree (matching the descendant count shown), so each bucket rolls
  // up into its ancestors, a record once per group.
  const subtreeRecordsByGroupKey = useMemo(() => {
    const getGroupAncestors = groupConfig?.getGroupAncestors
    if (!getGroupAncestors) return null

    const recordsByKey = new Map<string, Set<IResourceRecord>>()
    const add = (key: string, items: IResourceRecord[]) => {
      const bucket = recordsByKey.get(key)
      if (bucket) items.forEach(item => bucket.add(item))
      else recordsByKey.set(key, new Set(items))
    }
    listState.groups.forEach(group => {
      add(group.key, group.items)
      getGroupAncestors(group.key).forEach(ancestor =>
        add(ancestor, group.items),
      )
    })
    return new Map(
      Array.from(recordsByKey, ([key, records]) => [key, Array.from(records)]),
    )
  }, [groupConfig, listState.groups])

  const renderHeaderWithPresentation = React.useCallback(
    (group: ICardListGroup<IResourceRecord>, onToggle: () => void) => {
      const presentation = getGroupPresentation(group.key, group.count)
      const isMulti = mode === 'multi'
      const groupRecords = subtreeRecordsByGroupKey
        ? (subtreeRecordsByGroupKey.get(group.key) ?? [])
        : group.items
      const selectable = filterSelectable(
        groupRecords,
        selectedIds,
        isItemDisabled,
      )
      const allChecked =
        isMulti &&
        selectable.length > 0 &&
        selectable.every(item => selectedIds.has(item.id))
      const someChecked =
        isMulti &&
        !allChecked &&
        selectable.some(item => selectedIds.has(item.id))

      return (
        // Wrapped so CardList's last-row `[&>*]:border-b-0` lands here, keeping the band's bottom border.
        <div>
          <GroupHeader
            role="group"
            aria-label={group.label}
            className={classList({
              'tw:border-t': headersWithTopBorder.has(group.key),
            })}
          >
            {isMulti && (
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onCheckedChange={() => toggleGroup(selectable)}
                aria-label={Localization.translator.selectX(group.label)}
              />
            )}
            <GroupHeaderTrigger
              expanded={!group.isCollapsed}
              onClick={onToggle}
              ariaLabel={group.label}
              leading={<GroupIndent depth={group.depth} />}
              labelIcon={presentation?.labelIcon}
              label={presentation?.label ?? group.label}
              count={group.count}
            />
          </GroupHeader>
        </div>
      )
    },
    [
      getGroupPresentation,
      mode,
      selectedIds,
      toggleGroup,
      isItemDisabled,
      subtreeRecordsByGroupKey,
      headersWithTopBorder,
    ],
  )

  const defaultItemRenderer = React.useCallback(
    (record: IResourceRecord) => (
      <PickerItem record={record} subtitleKey={subtitleKey} />
    ),
    [subtitleKey],
  )

  const customItemRenderer = React.useCallback(
    (record: IResourceRecord) => {
      if (!renderItem) return null
      const isSelected = selectedIds.has(record.id)
      return renderItem(record, isSelected)
    },
    [selectedIds, renderItem],
  )

  const sourceItemRenderer = React.useCallback(
    (record: IResourceRecord) =>
      (getSourceRenderItem?.(record) ?? defaultItemRenderer)(record),
    [getSourceRenderItem, defaultItemRenderer],
  )

  const baseItemRenderer = renderItem
    ? customItemRenderer
    : getSourceRenderItem
      ? sourceItemRenderer
      : defaultItemRenderer

  const itemRenderer = React.useCallback(
    (
      record: IResourceRecord,
      _depth?: number,
      position?: ICardListItemPosition,
    ) => (
      <div
        className={classList({
          'tw:px-2.5 tw:py-px': true,
          'tw:pt-2.5': !!position?.isFirst,
          'tw:pb-2.5': !!position?.isLast,
        })}
      >
        {baseItemRenderer(record)}
      </div>
    ),
    [baseItemRenderer],
  )

  if (loading) return <PickerLoading />

  if (sortedRecords.length === 0) {
    return (
      <PickerCountRow
        items={sortedRecords}
        listState={activeListState}
        countLabel={countLabel}
        portalSelector={portalSelector}
      />
    )
  }

  return (
    <CardList
      role="listbox"
      ariaMultiselectable={mode === 'multi'}
      items={sortedRecords}
      listState={activeListState}
      initialScrollToItemId={firstSelectedId}
      header={
        <PickerCountRow
          items={sortedRecords}
          listState={activeListState}
          countLabel={countLabel}
          portalSelector={portalSelector}
        />
      }
      renderItem={itemRenderer}
      renderGroupHeader={renderGroupHeader ?? renderHeaderWithPresentation}
    />
  )
}

// Each list keeps its own collapse state through loading and an empty search.
const PickerList: React.FC<IProps> = props => {
  const { isTreeGrouping } = useResourcePickerContext()

  return isTreeGrouping ? (
    <PickerTree
      countLabel={props.countLabel}
      portalSelector={props.portalSelector}
    />
  ) : (
    <PickerGroupedList {...props} />
  )
}

export default PickerList
